// Platform super-admin: feature requests silently captured by the AI helper
// whenever an office user asks for something Fieldlink can't do yet.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Lightbulb, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "reviewed", label: "Reviewed" },
  { key: "planned", label: "Planned" },
  { key: "dismissed", label: "Dismissed" },
];

const STATUS_CLS = {
  new: "bg-orange-100 text-orange-800 border-orange-400",
  reviewed: "bg-sky-100 text-sky-800 border-sky-400",
  planned: "bg-emerald-100 text-emerald-800 border-emerald-400",
  dismissed: "bg-zinc-100 text-zinc-500 border-zinc-300",
};

export default function FeatureRequestsPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/platform/feature-requests", { params: { status: filter } })
      .then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const setStatus = async (fr, status) => {
    try {
      await api.patch(`/platform/feature-requests/${fr.id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === fr.id ? { ...x, status } : x)));
      setCounts((c) => ({ ...c, [fr.status]: Math.max(0, (c[fr.status] || 1) - 1), [status]: (c[status] || 0) + 1 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (fr) => {
    if (!window.confirm("Delete this feature request?")) return;
    try {
      await api.delete(`/platform/feature-requests/${fr.id}`);
      setItems((arr) => arr.filter((x) => x.id !== fr.id));
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="platform-feature-requests">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-xl flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#FF5F15]" /> Feature requests
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Captured automatically when an office user asks the AI helper for something Fieldlink can&apos;t do yet. Users are never told these are logged.
          </p>
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`px-3 py-1.5 border-2 text-[10px] font-bold uppercase tracking-wider transition-all ${
                filter === s.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"
              }`}
              data-testid={`fr-filter-${s.key}`}
            >
              {s.label}{s.key !== "all" && counts[s.key] != null ? ` · ${counts[s.key]}` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-zinc-100 animate-pulse border border-zinc-200" />)}</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-10 text-center" data-testid="fr-empty">
          <Lightbulb className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
          <div className="text-sm text-zinc-500">No feature requests {filter !== "all" ? `with status "${filter}"` : "captured yet"}.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((fr) => (
            <div key={fr.id} className="border border-zinc-300 bg-white p-4 flex items-start gap-3" data-testid={`fr-row-${fr.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm">{fr.summary}</div>
                <div className="text-xs text-zinc-600 mt-1 italic">&ldquo;{fr.question}&rdquo;</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1.5">
                  {fr.org_name} · {fr.user_name} · {new Date(fr.created_at).toLocaleString("en-NZ")}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${STATUS_CLS[fr.status] || STATUS_CLS.new}`}>
                  {fr.status}
                </span>
                <select
                  value={fr.status}
                  onChange={(e) => setStatus(fr, e.target.value)}
                  className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white"
                  data-testid={`fr-status-${fr.id}`}
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="planned">Planned</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <button onClick={() => remove(fr)} className="text-zinc-400 hover:text-red-600" data-testid={`fr-delete-${fr.id}`} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
